import { useCallback, useEffect, useState } from 'react';
import { FeedTabs } from '../components/FeedTabs';
import { Reveal } from '../components/ui/Reveal';
import { EmptyState } from '../components/EmptyState';
import {
  fetchRequests,
  getApiErrorInfo,
  respondToRequest,
  type RequestListItem,
  type RequestType,
} from '../lib/api';
import { useAppStore } from '../store/useAppStore';
import { haptic } from '../utils/haptic';
import { RequestCard } from './requests/RequestCard';
import { SkeletonRequestCard } from '../components/SkeletonRequestCard';
import { RequestDetailView } from './requests/RequestDetailView';

const REQUEST_TYPES: { id: RequestType; label: string }[] = [
  { id: 'developer', label: 'Разработчик' },
  { id: 'partner', label: 'Партнёр' },
  { id: 'investor', label: 'Инвестор' },
];

function openTelegramContact(username: string): boolean {
  const clean = username.replace(/^@/, '').trim();
  if (!clean) return false;
  const url = `https://t.me/${clean}`;
  const tg = (window as unknown as { Telegram?: { WebApp?: { openTelegramLink?: (u: string) => void } } }).Telegram?.WebApp;
  if (tg?.openTelegramLink) {
    tg.openTelegramLink(url);
  } else {
    window.open(url, '_blank', 'noopener,noreferrer');
  }
  return true;
}

export function RequestsScreen() {
  const [list, setList] = useState<RequestListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState<RequestType | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [respondedIds, setRespondedIds] = useState<Set<string>>(new Set());
  const addNotification = useAppStore((s) => s.addNotification);
  const setScreen = useAppStore((s) => s.setScreen);
  const setViewProfileUserId = useAppStore((s) => s.setViewProfileUserId);

  const openAuthorProfile = useCallback(
    (authorId: number) => {
      if (!authorId) return;
      setViewProfileUserId(authorId);
      setScreen('user-profile');
    },
    [setScreen, setViewProfileUserId]
  );

  const loadList = useCallback(async () => {
    setLoading(true);
    try {
      const fetched = await fetchRequests({ type: filterType ?? undefined });
      setList(fetched.filter((item) => item.status === 'approved'));
    } finally {
      setLoading(false);
    }
  }, [filterType]);

  useEffect(() => {
    loadList();
  }, [loadList]);

  const handleRespond = useCallback(
    async (id: string) => {
      const request = list.find((item) => item.id === id);
      let shouldMarkResponded = false;
      try {
        const result = await respondToRequest(id);
        shouldMarkResponded = true;
        if (result.status === 'duplicate') {
          addNotification('Вы уже откликались недавно');
        } else {
          addNotification('Вы откликнулись на заявку');
        }
      } catch (error) {
        const info = getApiErrorInfo(error);
        addNotification(info.message || 'Не удалось отправить отклик');
      }

      if (!shouldMarkResponded) {
        return;
      }
      setRespondedIds((prev) => new Set(prev).add(id));

      if (request?.author?.username && openTelegramContact(request.author.username)) {
        return;
      }
      if (request?.author?.id) {
        openAuthorProfile(request.author.id);
      }
    },
    [addNotification, list, openAuthorProfile]
  );

  if (selectedId) {
    return (
      <RequestDetailView
        requestId={selectedId}
        onBack={() => setSelectedId(null)}
        onRespond={handleRespond}
        respondedIds={respondedIds}
        onOpenAuthorProfile={openAuthorProfile}
      />
    );
  }

  const itemsWithResponded = list.map((item) => ({
    ...item,
    i_responded: item.i_responded || respondedIds.has(item.id),
  }));

  return (
    <div className="requests-screen">
      <FeedTabs />
      <header className="header">
        <h1 className="header-title">Заявки</h1>
        <p className="header-subtitle">Публичная витрина</p>
      </header>
      <main className="main">
        <div className="container">
          <div className="requests-filter-bar">
            {REQUEST_TYPES.map(({ id, label }) => (
              <button
                key={id}
                type="button"
                className={`requests-filter-chip ${filterType === id ? 'requests-filter-chip--active' : ''}`}
                aria-pressed={filterType === id}
                onClick={() => {
                  haptic.select();
                  setFilterType((value) => (value === id ? null : id));
                }}
              >
                {label}
              </button>
            ))}
          </div>

          {loading ? (
            <>
              <SkeletonRequestCard />
              <SkeletonRequestCard />
              <SkeletonRequestCard />
            </>
          ) : itemsWithResponded.length === 0 ? (
            <EmptyState type="requests" />
          ) : (
            <div className="requests-list">
              {itemsWithResponded.map((item, i) => (
                <Reveal key={item.id} delay={i * 60}>
                  <RequestCard
                    request={item}
                    onClick={() => setSelectedId(item.id)}
                    onRespond={handleRespond}
                  />
                </Reveal>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
